// حالت حافظه‌ایِ دمو - جایگزینِ Supabase وقتی isDemoMode روشنه.
// همه‌چیز این‌جا mutable و module-level است؛ با رفرشِ صفحه از نو از fixtures ساخته میشه.
import * as F from './fixturesActive';
import { phoneKey } from '../lib/contacts';
import { TERMS_VERSION } from '../legal/terms';
import type {
  AdminAction, AdminItemDetail, AppNotification, AvailabilityCell, Business, Condition,
  DeclineReason, DiscoverMarks, DismissedDuplicate, DuplicateCandidate, EventEditDraft, EventItem,
  EventOccurrence, EventVersion, Friend, Group,
  ImportedContact, ItemMerge, ItemSource, ManualFriendDraft, MergedItem, Message,
  MemberRef, MyPhone, NotifPrefs, Plan, Profile, RankedSlot, Recipient, ResponseKind, Signal,
  SignalDraft, SignalEditDraft, SignalPhoto, SignalVersion, StartVerifyResult, SuggestionDraft,
  Tag, UserId,
  VerifyPhoneResult,
} from '../domain/types';

/** آینه‌ی notification_category (مایگریشن 0008) - جدا از miscRepo تا وارد کردنِ چرخه‌ای پیش نیاد. */
const categoryOfKind = (kind: string): 'friend_request' | 'signal' | 'event' => {
  if (kind === 'friend_request' || kind === 'friend_accepted' || kind === 'contact_joined') return 'friend_request';
  if (kind === 'signal_received' || kind === 'signal_changed' || kind === 'signal_cancelled') return 'signal';
  return 'event';
};
const ACTIONABLE_KINDS = ['signal_received', 'friend_request'];
/** همان استثنای `markSeen` در miscRepo: خبرِ گفتگو/عکس فقط با باز کردنِ سربرگش می‌رود. */
const TAB_SEEN_KINDS = ['signal_message', 'signal_photo_added'];

/** چندتا آدمِ دیگه که هنوز دوست نیستن، فقط برای دموی «درخواست دوستی فرستادن». */
const DISCOVERABLE: Profile[] = [
  { id: 'demo-reza', username: 'reza', displayName: 'رضا', avatarEmoji: '🎯', city: 'اتاوا', country: 'کانادا', statusText: null, avatarPath: null, avatarUrl: null },
];

interface State {
  profile: Profile;
  friends: Friend[];
  groups: Group[];
  signals: Signal[];
  /** تاریخچه‌ی نسخه‌ها - آینه‌ی جدولِ signal_versions (مایگریشن 0072). */
  signalVersions: Record<string, SignalVersion[]>;
  recipients: Record<string, Recipient[]>;
  plans: Record<string, Plan>;
  messages: Record<string, Message[]>;
  /** عکس‌های بعد از برنامه (مایگریشن 0141). دمو با فهرستِ خالی شروع می‌کند. */
  signalPhotos: Record<string, SignalPhoto[]>;
  notifications: AppNotification[];
  contacts: ImportedContact[];
  /** شماره‌های خودم؛ دقیقاً یکیشون پیش‌فرضه - مثل قانونِ مایگریشن 0048. */
  myPhones: MyPhone[];
  tags: Tag[];
  favorites: { kind: 'event' | 'business'; refId: string }[];
  /** تگ‌های دنبال‌شده (مایگریشن 0094) — فقط شناسه، مثل خودِ جدول. */
  tagFollows: number[];
  events: EventItem[];
  businesses: Business[];
  availability: AvailabilityCell[];
  notifPrefs: NotifPrefs;
  /**
   * ستون‌هایی از جدولِ events که فقط ادمین می‌بینه (مایگریشن 0051).
   *
   * جدا از خودِ رخداد نگه داشته می‌شن چون EventItem مدلِ چیزیه که کاربرِ عادی
   * می‌بینه؛ فلگ و ادغام اون‌جا جا ندارن.
   */
  adminMeta: Record<string, {
    flaggedAt?: string | null; flagNote?: string | null;
    mergedInto?: string | null; mergedAt?: string | null; deletedFrom?: string | null;
  }>;
  adminActions: (AdminAction & { eventId: string })[];
  /** `moved` همون کاریه که event_merges.moved توی دیتابیس می‌کنه: مصالحِ undo. */
  adminMerges: (ItemMerge & { moved: { statuses: { id: string; status: string }[]; favorites: string[] } })[];
  /** جفت‌هایی که «یکی نیستن» - آینه‌ی event_not_duplicates (مایگریشن 0053). */
  notDuplicates: { a: string; b: string; note?: string | null; at: string }[];
  /**
   * آیتم‌هایی که از ترکیب در اومدن - آینه‌ی `metadata.combined_from`.
   *
   * توی دیتابیس روی خودِ ردیف می‌شینه، ولی این‌جا EventItem مدلِ چیزیه که
   * کاربرِ عادی می‌بینه و metadata اون‌جا جا نداره - پس مثلِ adminMeta بیرون
   * نگه داشته می‌شه.
   */
  combinedIds: string[];
  /** زمان/مکان‌های اضافه - آینه‌ی event_occurrences (مایگریشن 0054). */
  occurrences: (EventOccurrence & { eventId: string })[];
  nextId: { signal: number; group: number; tag: number; contact: number; notification: number; recipient: number; message: number; photo: number; event: number; admin: number };
}

/** همون کاری که `snapshot_signal` توی دیتابیس می‌کنه: عکسِ فیلدهای ویرایش‌پذیر. */
const snapshot = (sig: Signal): SignalVersion => ({
  signalId: sig.id, version: sig.version, kind: sig.kind, title: sig.title, note: sig.note,
  categoryId: sig.categoryId, timeMode: sig.timeMode, startsAt: sig.startsAt, endsAt: sig.endsAt,
  dateOnly: sig.dateOnly, approxLabel: sig.approxLabel, placeName: sig.placeName,
  costNote: sig.costNote, chainLimit: sig.chainLimit, createdAt: new Date().toISOString(),
});

/**
 * شناسه‌ی عددیِ کوتاه (مایگریشن 0051). توی دیتابیس از یه sequence می‌آد؛
 * این‌جا فقط باید پایدار و یکتا باشه - و برای همه‌ی نسخه‌های یه آیتم یکی.
 */
function withShortIds(events: EventItem[], businesses: Business[]): void {
  const seen = new Map<string, number>();
  let next = 1000;
  for (const e of events) {
    const root = e.rootId ?? e.id;
    if (!seen.has(root)) { seen.set(root, next); next += 1; }
    e.shortId = seen.get(root);
  }
  for (const b of businesses) { b.shortId = next; next += 1; }
}

function freshState(): State {
  const events = F.demoEvents();
  const businesses = F.demoBusinesses();
  withShortIds(events, businesses);
  return {
    // دمو تورِ محصول است، نه ثبت‌نام: گیتِ پذیرشِ شرایط نباید سرِ راهش
    // بنشیند. این‌جا و نه در خودِ فیکسچر، چون `fixtures.ts` مستقیم زیرِ
    // `node --test` است و آن‌جا importِ اجراییِ بی‌پسوند رد می‌شود.
    profile: { ...F.DEMO_PROFILE, termsVersion: TERMS_VERSION },
    friends: F.demoFriends(),
    groups: F.demoGroups(),
    signals: F.demoSignals(),
    // هر سیگنالِ دمو یه نسخه داره: همون چیزی که هست. تا کاربر ویرایش نکرده،
    // تاریخچه هم چیزی برای گفتن نداره.
    signalVersions: Object.fromEntries(F.demoSignals().map((sig) => [sig.id, [snapshot(sig)]])),
    recipients: F.demoRecipients(),
    plans: F.demoPlans(),
    messages: F.demoMessages(),
    signalPhotos: {},
    notifications: F.demoNotifications(),
    contacts: F.demoContacts(),
    myPhones: [
      { value: '6135550100', isDefault: true },
      { value: '4385550177', isDefault: false },
    ],
    tags: F.demoTags(),
    favorites: [],
    tagFollows: [],
    events,
    businesses,
    availability: F.demoAvailability(),
    notifPrefs: { ...F.DEMO_NOTIF_PREFS },
    adminMeta: {},
    adminActions: [],
    adminMerges: [],
    notDuplicates: [],
    combinedIds: [],
    occurrences: [],
    nextId: { signal: 100, group: 100, tag: 100, contact: 100, notification: 100, recipient: 100, message: 100, photo: 100, event: 100, admin: 1 },
  };
}

let s = freshState();

const dataUrl = (base64: string) => `data:image/jpeg;base64,${base64}`;

/** یه سطر توی دفترِ کارهای ادمین - آینه‌ی جدولِ admin_actions (مایگریشن 0051). */
function logAdmin(
  eventId: string, action: AdminAction['action'], detail: Record<string, unknown>,
): void {
  s.adminActions.push({
    id: s.adminActions.length + 1, action, detail, eventId,
    createdAt: new Date().toISOString(), actorName: s.profile.displayName,
  });
}

export const demoStore = {
  /** بازگشت به داده‌ی اولیه - «خروج» توی حالت دمو یعنی همین، نه واقعاً logout. */
  reset(): void { s = freshState(); },

  profile: {
    me: (): Profile => ({ ...s.profile }),
    createMine: (username: string, displayName: string,
      extra?: {
        acceptedTerms?: boolean; city?: string; country?: string; cityId?: number | null;
      }): Profile => {
      s.profile = {
        ...s.profile, username: username || s.profile.username, displayName,
        ...(extra?.city ? { city: extra.city } : {}), ...(extra?.country ? { country: extra.country } : {}),
        ...(extra?.cityId ? { cityId: extra.cityId } : {}),
      };
      return { ...s.profile };
    },
    acceptTerms: (): Profile => {
      s.profile = { ...s.profile, termsVersion: TERMS_VERSION };
      return { ...s.profile };
    },
    byUsername: (username: string): Profile | null => {
      const all = [s.profile, ...s.friends.map((f) => f.profile), ...DISCOVERABLE];
      return all.find((p) => p.username.toLowerCase() === username.toLowerCase()) ?? null;
    },
    updateMine: (patch: {
      displayName?: string; city?: string; country?: string;
      cityId?: number | null; avatarPath?: string | null; statusText?: string | null;
    }): void => {
      if (patch.statusText !== undefined) s.profile.statusText = patch.statusText;
      if (patch.displayName !== undefined) s.profile.displayName = patch.displayName;
      if (patch.city !== undefined) s.profile.city = patch.city;
      if (patch.country !== undefined) s.profile.country = patch.country;
      if (patch.cityId !== undefined) s.profile.cityId = patch.cityId;
      if (patch.avatarPath !== undefined) {
        s.profile.avatarPath = patch.avatarPath;
        // مسیرها این‌جا خودشون data: URL هستن (uploadAvatar پایین) پس مستقیم قابل‌نمایشن.
        s.profile.avatarUrl = patch.avatarPath;
      }
    },
    uploadAvatar: (base64: string): string => dataUrl(base64),
    removeAvatar: (): void => undefined,
  },

  friends: {
    list: (): Friend[] => s.friends.map((f) => ({ ...f })),
    request: (username: string): 'sent' | 'not_found' | 'already' => {
      const target = DISCOVERABLE.find((p) => p.username.toLowerCase() === username.toLowerCase());
      if (!target) return 'not_found';
      if (s.friends.some((f) => f.profile.id === target.id)) return 'already';
      s.friends.push({ friendshipId: Date.now(), profile: target, status: 'pending_out' });
      return 'sent';
    },
    /**
     * حالتِ نمایش شماره و ایمیلِ کسی رو نداره — و ساختنِ یه دفترچه‌ی جعلی
     * فقط برای این، بیشتر از چیزی که به دست می‌ده هزینه داره. پس همیشه
     * «پیدا نشد»، که پیامِ درستی هم هست.
     */
    requestByContact: (_value: string): 'not_found' => 'not_found',
    accept: (friendshipId: number): void => {
      const f = s.friends.find((x) => x.friendshipId === friendshipId);
      if (f) f.status = 'accepted';
    },
    decline: (friendshipId: number): void => {
      s.friends = s.friends.filter((x) => x.friendshipId !== friendshipId);
    },
    remove: (friendshipId: number): void => {
      s.friends = s.friends.filter((x) => x.friendshipId !== friendshipId);
    },
  },

  groups: {
    list: (): Group[] => s.groups.map((g) => ({ ...g, memberUserIds: [...g.memberUserIds], memberContactIds: [...g.memberContactIds] })),
    create: (myId: UserId, name: string, emoji: string): number => {
      const id = s.nextId.group++;
      s.groups.push({ id, name, emoji, ownerId: myId, memberUserIds: [], memberContactIds: [] });
      return id;
    },
    addMember: (groupId: number, ref: MemberRef): void => {
      const g = s.groups.find((x) => x.id === groupId);
      if (!g) return;
      if (ref.kind === 'user') { if (!g.memberUserIds.includes(ref.userId)) g.memberUserIds.push(ref.userId); }
      else if (!g.memberContactIds.includes(ref.contactId)) g.memberContactIds.push(ref.contactId);
    },
    removeMember: (groupId: number, ref: MemberRef): void => {
      const g = s.groups.find((x) => x.id === groupId);
      if (!g) return;
      if (ref.kind === 'user') g.memberUserIds = g.memberUserIds.filter((id) => id !== ref.userId);
      else g.memberContactIds = g.memberContactIds.filter((id) => id !== ref.contactId);
    },
    rename: (groupId: number, name: string, emoji: string): void => {
      const g = s.groups.find((x) => x.id === groupId);
      if (g) { g.name = name; g.emoji = emoji; }
    },
    remove: (groupId: number): void => { s.groups = s.groups.filter((x) => x.id !== groupId); },
  },

  signals: {
    // `cancelled` این‌جا هم هست، به همون دلیلِ signalRepo: لغو باید دیده شه.
    // سیگنالِ **حذف‌شده** اصلاً از `s.signals` برداشته می‌شه، پس فیلتری
    // لازم نداره — همون کاری که RLS با `deleted_at` می‌کنه.
    incoming: (uid: UserId): Signal[] =>
      s.signals.filter((sig) => sig.creatorId !== uid
        && ['active', 'confirmed', 'cancelled'].includes(sig.status)),
    mine: (uid: UserId): Signal[] =>
      s.signals.filter((sig) => sig.creatorId === uid
        && ['active', 'confirmed', 'completed', 'cancelled'].includes(sig.status)),
    byId: (id: string): Signal | null => {
      const sig = s.signals.find((x) => x.id === id);
      return sig ? { ...sig } : null;
    },
    recipients: (signalId: string): Recipient[] => (s.recipients[signalId] ?? []).map((r) => ({ ...r })),
    createAndSend: (draft: SignalDraft, myId: string): Signal => {
      const id = `demo-new-${s.nextId.signal++}`;
      const sig: Signal = {
        id, creatorId: myId, creator: { ...s.profile }, kind: draft.kind, title: draft.title,
        note: draft.note ?? null, categoryId: draft.tagIds[0] ?? null,
        tagIds: draft.tagIds, timeMode: draft.timeMode,
        startsAt: draft.startsAt?.toISOString() ?? null,
        endsAt: draft.endsAt?.toISOString() ?? null, dateOnly: draft.dateOnly ?? false,
        approxLabel: draft.approxLabel ?? null,
        placeName: draft.placeName ?? null, costNote: draft.costNote ?? null,
        chainLimit: draft.chainLimit, status: 'active', version: 1, shareToken: id,
        createdAt: new Date().toISOString(),
        counts: { in: 0, out: 0, maybe: 0, pending: draft.recipientIds.length, conditional: 0 },
      };
      s.signals.unshift(sig);
      s.signalVersions[id] = [snapshot(sig)];
      const allProfiles = [s.profile, ...s.friends.map((f) => f.profile), ...DISCOVERABLE];
      s.recipients[id] = draft.recipientIds.map((rid) => ({
        id: s.nextId.recipient++, signalId: id, recipientId: rid,
        profile: allProfiles.find((p) => p.id === rid), via: 'direct', response: 'pending',
      }));
      return { ...sig };
    },

    /**
     * ویرایش — همون کارِ تریگرهای مایگریشن 0072، دستی: شماره‌ی نسخه یکی بالا
     * می‌ره و یه عکسِ تازه ته تاریخچه اضافه می‌شه. اعلان در حالتِ دمو معنی
     * نداره (کسی اون طرف نیست).
     */
    update: (id: string, draft: SignalEditDraft): Signal => {
      const sig = s.signals.find((x) => x.id === id);
      if (!sig) throw new Error('signal not found');
      Object.assign(sig, {
        kind: draft.kind, title: draft.title, note: draft.note ?? null,
        categoryId: draft.tagIds[0] ?? null, tagIds: draft.tagIds, timeMode: draft.timeMode,
        startsAt: draft.startsAt?.toISOString() ?? null,
        endsAt: draft.endsAt?.toISOString() ?? null,
        dateOnly: draft.dateOnly ?? false, approxLabel: draft.approxLabel ?? null,
        placeName: draft.placeName ?? null, costNote: draft.costNote ?? null,
        chainLimit: draft.chainLimit, version: sig.version + 1,
      });
      (s.signalVersions[id] ??= []).push(snapshot(sig));
      return { ...sig };
    },

    versions: (id: string): SignalVersion[] =>
      (s.signalVersions[id] ?? []).map((v) => ({ ...v })),

    /** همون تصمیمِ `remove_signal`: پایه داشتیم → لغو، نداشتیم → حذف. */
    remove: (id: string): 'cancelled' | 'deleted' => {
      const hasIn = (s.recipients[id] ?? []).some((r) => r.response === 'in');
      if (hasIn) {
        const sig = s.signals.find((x) => x.id === id);
        if (sig) sig.status = 'cancelled';
        return 'cancelled';
      }
      s.signals = s.signals.filter((x) => x.id !== id);
      return 'deleted';
    },

    addRecipients: (id: string, recipientIds: string[]): void => {
      const allProfiles = [s.profile, ...s.friends.map((f) => f.profile), ...DISCOVERABLE];
      const rows = (s.recipients[id] ??= []);
      for (const rid of recipientIds) {
        if (rows.some((r) => r.recipientId === rid)) continue;
        rows.push({
          id: s.nextId.recipient++, signalId: id, recipientId: rid,
          profile: allProfiles.find((p) => p.id === rid), via: 'direct', response: 'pending',
        });
      }
    },

    removeRecipient: (rowId: number): void => {
      for (const key of Object.keys(s.recipients)) {
        s.recipients[key] = s.recipients[key].filter((r) => r.id !== rowId || r.response === 'in');
      }
    },
    respond: (signalId: string, uid: string, response: ResponseKind, condition?: Condition, declineReason?: DeclineReason): void => {
      const sig = s.signals.find((x) => x.id === signalId);
      if (sig?.counts) {
        const prev = sig.myResponse ?? 'pending';
        if (prev in sig.counts) (sig.counts as Record<string, number>)[prev] -= 1;
        if (response in sig.counts) (sig.counts as Record<string, number>)[response] += 1;
      }
      if (sig) {
        sig.myResponse = response;
        sig.myCondition = condition ?? null;
        sig.myDeclineReason = response === 'out' ? declineReason ?? null : null;
      }
      const rec = (s.recipients[signalId] ?? []).find((r) => r.recipientId === uid);
      if (rec) { rec.response = response; rec.condition = condition ?? null; rec.declineReason = declineReason ?? null; }
    },
    confirmPlan: (signalId: string, finalStartsAt: Date, finalPlace?: string): Plan => {
      const plan: Plan = {
        signalId, finalStartsAt: finalStartsAt.toISOString(), finalPlace: finalPlace ?? null,
        reminderMin: 60, outcome: 'pending', shareScope: 'friends',
      };
      s.plans[signalId] = plan;
      const sig = s.signals.find((x) => x.id === signalId);
      if (sig) sig.status = 'confirmed';
      return { ...plan };
    },
    plan: (signalId: string): Plan | null => (s.plans[signalId] ? { ...s.plans[signalId] } : null),
    setOutcome: (signalId: string, outcome: Plan['outcome'], shareScope: Plan['shareScope']): void => {
      if (s.plans[signalId]) { s.plans[signalId].outcome = outcome; s.plans[signalId].shareScope = shareScope; }
      const sig = s.signals.find((x) => x.id === signalId);
      if (sig) sig.status = outcome === 'cancelled' ? 'cancelled' : 'completed';
    },
    messages: (signalId: string): Message[] => (s.messages[signalId] ?? []).map((m) => ({ ...m })),
    sendMessage: (signalId: string, uid: string, body: string): void => {
      (s.messages[signalId] ??= []).push({
        id: s.nextId.message++, signalId, senderId: uid, senderName: s.profile.displayName,
        kind: 'text', body, createdAt: new Date().toISOString(),
      });
    },
    photos: (signalId: string): SignalPhoto[] =>
      (s.signalPhotos[signalId] ?? []).map((p) => ({ ...p })),
    /** در دمو خودِ عکس همان data-URL است — باکتی در کار نیست (مثلِ uploadImage). */
    addPhoto: (signalId: string, uid: string, base64: string): void => {
      (s.signalPhotos[signalId] ??= []).unshift({
        id: s.nextId.photo++, signalId, uploaderId: uid,
        uploaderName: s.profile.displayName,
        path: `${signalId}/demo-${s.nextId.photo}.jpg`,
        url: dataUrl(base64), createdAt: new Date().toISOString(),
      });
    },
    removePhoto: (id: number): void => {
      for (const key of Object.keys(s.signalPhotos)) {
        s.signalPhotos[key] = s.signalPhotos[key].filter((p) => p.id !== id);
      }
    },
  },

  publicSignals: {
    byToken: (token: string) => {
      const t = token === 'demo' ? 's1' : token.replace(/^demo-/, '');
      const sig = s.signals.find((x) => x.id === t || x.shareToken === token);
      if (!sig) return null;
      return {
        id: sig.id, title: sig.title, kind: sig.kind, note: sig.note ?? undefined,
        time_mode: sig.timeMode, starts_at: sig.startsAt ?? undefined,
        ends_at: sig.endsAt ?? undefined, date_only: sig.dateOnly ?? false,
        approx_label: sig.approxLabel ?? undefined, place_name: sig.placeName ?? undefined,
        cost_note: sig.costNote ?? undefined, status: sig.status,
        creator_name: sig.creator?.displayName ?? 'یکی از دوستات',
      };
    },
  },

  tags: {
    list: (): Tag[] => s.tags.map((t) => ({ ...t })),
    create: (name: string): Tag => {
      const label = name.trim();
      const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `tag-${Date.now()}`;
      const tag: Tag = { id: s.nextId.tag++, slug, emoji: '', labelFa: label, labelEn: label };
      s.tags.push(tag);
      return { ...tag };
    },
  },

  favorites: {
    list: (): { kind: 'event' | 'business'; refId: string }[] => s.favorites.map((f) => ({ ...f })),
    set: (kind: 'event' | 'business', refId: string, on: boolean): void => {
      if (on) {
        if (!s.favorites.some((f) => f.kind === kind && f.refId === refId)) s.favorites.push({ kind, refId });
      } else {
        s.favorites = s.favorites.filter((f) => !(f.kind === kind && f.refId === refId));
      }
    },
  },

  tagFollows: {
    list: (): number[] => [...s.tagFollows],
    set: (tagId: number, on: boolean): void => {
      if (on) {
        if (!s.tagFollows.includes(tagId)) s.tagFollows.push(tagId);
      } else {
        s.tagFollows = s.tagFollows.filter((id) => id !== tagId);
      }
    },
  },

  events: {
    upcoming: (): EventItem[] => s.events
      .filter((e) => (e.status ?? 'published') === 'published')
      .map((e) => ({ ...e })),
    byId: (id: string): EventItem | null => {
      const e = s.events.find((x) => x.id === id);
      return e ? { ...e } : null;
    },
    /** توی دمو هیچ رخدادی چند اجرا نداره - همون حالتِ ۹۰ درصدی. */
    occurrences: (eventId: string): EventOccurrence[] =>
      s.occurrences.filter((o) => o.eventId === eventId).map(({ eventId: _, ...o }) => o),
    /**
     * توی دمو پیشنهاد واقعاً یه ردیف می‌سازه — وگرنه پینِ «رخدادهای من»
     * (مایگریشن 0077) توی دمو هیچ‌وقت چیزی نداشت و کاربر فکر می‌کرد کار
     * نمی‌کنه. همون وضعیتِ واقعی: منتظرِ بازبینی، به نامِ خودش.
     */
    suggest: (uid: UserId, cityId: number, d: SuggestionDraft): void => {
      s.events.push({
        id: `evt-mine-${s.nextId.event++}`,
        title: d.name.trim(), description: d.description.trim() || null,
        categoryId: d.tagIds[0] ?? null, tagIds: d.tagIds,
        startsAt: d.startsAt?.toISOString() ?? null,
        endsAt: d.endsAt?.toISOString() ?? null,
        isFree: d.isFree, isPromoted: false, url: d.url,
        ticketUrl: d.ticketUrl ?? null, imageUrl: d.imageUrl ?? null,
        cityId, kind: 'event', status: 'pending_review',
        submittedBy: uid, isOrganizer: d.isOrganizer ?? false,
        createdAt: new Date().toISOString(),
      });
    },

    /** فهرستِ پین — همون قاعده‌ی سرور: هرچی که خودم فرستادم، در هر وضعیتی. */
    mine: (): EventItem[] =>
      s.events.filter((e) => e.submittedBy === s.profile.id).map((e) => ({ ...e })),

    /**
     * ویرایشِ رخدادِ خودم. همون دو شاخه‌ی `save_own_event`: برگزارکننده
     * مستقیم، خبررسانِ یه رخدادِ منتشرشده از راهِ صف.
     */
    saveOwn: (eventId: string, d: EventEditDraft, editNote?: string) => {
      const row = s.events.find((e) => e.id === eventId);
      if (!row) return { id: eventId, needsReview: false };
      const needsReview = row.status === 'published' && !row.isOrganizer;
      if (needsReview) {
        const id = demoStore.events.proposeEdit(row.rootId ?? row.id, d, editNote);
        const made = s.events.find((e) => e.id === id);
        if (made) made.submittedBy = s.profile.id;
        return { id, needsReview };
      }
      Object.assign(row, {
        title: d.title, description: d.description ?? null,
        categoryId: d.tagIds[0] ?? null, tagIds: d.tagIds,
        startsAt: d.startsAt?.toISOString() ?? null,
        endsAt: d.endsAt?.toISOString() ?? null,
        isFree: d.isFree, priceNote: d.priceNote ?? null, url: d.url,
        ticketUrl: d.ticketUrl ?? null, imageUrl: d.imageUrl ?? null,
        editNote: editNote ?? null, editedBy: s.profile.id,
        status: row.status === 'published' ? 'published' : 'pending_review',
      });
      return { id: row.id, needsReview };
    },

    /** توی دمو چیزی آپلود نمی‌شه؛ خودِ عکس به‌صورتِ data-url نگه داشته می‌شه. */
    uploadImage: (base64: string): string => dataUrl(base64),

    /** پس گرفتن: کلِ زنجیره، همون قاعده‌ی `withdraw_own_event`. */
    withdrawOwn: (eventId: string): void => {
      const row = s.events.find((e) => e.id === eventId);
      if (!row) return;
      const root = row.rootId ?? row.id;
      for (const e of s.events) {
        if ((e.rootId ?? e.id) !== root) continue;
        if (e.status === 'published' || e.status === 'pending_review' || e.status === 'rejected') {
          e.status = 'withdrawn';
        }
      }
    },

    proposeEdit: (rootId: string, d: EventEditDraft, editNote?: string): string => {
      const root = s.events.find((e) => (e.rootId ?? e.id) === rootId);
      const thread = s.events.filter((e) => (e.rootId ?? e.id) === rootId);
      const nextVersion = Math.max(0, ...thread.map((e) => e.version ?? 1)) + 1;
      const id = `evt-edit-${s.nextId.event++}`;
      s.events.push({
        // نسخه‌ی تازه همون شناسه‌ی عددیِ آیتم رو می‌گیره - همون کاری که
        // تریگرِ events_short_id_fill توی دیتابیس می‌کنه.
        id, shortId: root?.shortId, title: d.title,
        description: d.description ?? null, categoryId: d.tagIds[0] ?? null,
        tagIds: d.tagIds,
        venueName: d.venueName ?? root?.venueName, lat: d.venueLat ?? root?.lat, lng: d.venueLng ?? root?.lng,
        startsAt: d.startsAt?.toISOString() ?? null, endsAt: d.endsAt?.toISOString() ?? null,
        isFree: d.isFree, priceNote: d.priceNote ?? null, isPromoted: false, url: d.url,
        rootId, version: nextVersion, status: 'pending_review',
        editNote: editNote ?? null, editedBy: s.profile.id,
      });
      return id;
    },

    versions: (rootId: string): EventVersion[] => {
      const nameOf = (uid?: string | null) => {
        if (!uid) return null;
        if (uid === s.profile.id) return s.profile.displayName;
        return s.friends.find((f) => f.profile.id === uid)?.profile.displayName ?? null;
      };
      const rows = s.events
        .filter((e) => (e.rootId ?? e.id) === rootId)
        .map((e) => ({
          ...e, status: e.status ?? 'published',
          editedByName: nameOf(e.editedBy), reviewedByName: nameOf(e.reviewedBy),
        }));
      return rows.sort((a, b) => {
        const pending = Number(b.status === 'pending_review') - Number(a.status === 'pending_review');
        return pending !== 0 ? pending : (b.version ?? 0) - (a.version ?? 0);
      });
    },

    approve: (versionId: string): void => {
      const ver = s.events.find((e) => e.id === versionId);
      if (!ver) return;
      const root = ver.rootId ?? ver.id;
      for (const e of s.events) {
        if ((e.rootId ?? e.id) === root && e.status === 'published' && e.id !== versionId) {
          e.status = 'superseded';
        }
      }
      ver.status = 'published';
      ver.reviewedBy = s.profile.id;
      ver.reviewedAt = new Date().toISOString();
    },

    reject: (versionId: string, reason: string): void => {
      const ver = s.events.find((e) => e.id === versionId);
      if (!ver) return;
      ver.status = 'rejected';
      ver.reviewNote = reason;
      ver.reviewedBy = s.profile.id;
      ver.reviewedAt = new Date().toISOString();
    },
  },

  /**
   * جعبه‌ابزارِ ادمین (مایگریشن 0051) - همون رفتار، روی حافظه.
   *
   * پروفایلِ دمو ادمینه (fixtures.ts)، پس این صفحه‌ها توی دمو واقعاً باز
   * می‌شن؛ بدونِ این‌ها هر کدوم یه صفحه‌ی خالی می‌شد.
   */
  admin: {
    detail: (id: string): AdminItemDetail | null => {
      const e = s.events.find((x) => x.id === id);
      if (!e) return null;
      const meta = s.adminMeta[id] ?? {};
      const target = meta.mergedInto ? s.events.find((x) => x.id === meta.mergedInto) : null;
      return {
        ...e,
        shortId: e.shortId ?? 0,
        source: 'demo',
        externalId: null,
        metadata: { demo: true, note: 'دادهٔ دمو - چیزی از منبعِ واقعی گرفته نشده' },
        flaggedAt: meta.flaggedAt ?? null,
        flagNote: meta.flagNote ?? null,
        flaggedByName: meta.flaggedAt ? s.profile.displayName : null,
        mergedInto: meta.mergedInto ?? null,
        mergedAt: meta.mergedAt ?? null,
        mergedByName: meta.mergedInto ? s.profile.displayName : null,
        mergedIntoShortId: target?.shortId ?? null,
        raw: { ...e } as unknown as Record<string, unknown>,
      };
    },

    byShortId: (shortId: number): { id: string; title: string; status: string } | null => {
      const rows = s.events.filter((e) => e.shortId === shortId);
      if (!rows.length) return null;
      const best = [...rows].sort((a, b) => (
        Number(b.status === 'published') - Number(a.status === 'published')
        || (b.version ?? 0) - (a.version ?? 0)
      ))[0];
      return { id: best.id, title: best.title, status: best.status ?? 'published' };
    },

    /**
     * چهار نشانه‌ی ادمینِ صفحه‌ی کشف - نگاه کن به `adminRepo.discoverMarks`.
     *
     * توی دمو شهری نیست چون کلِ داده‌ی دمو یک شهر است؛ تابعِ واقعی شهر
     * می‌گیرد و صداکننده‌ها هم می‌دهند.
     *
     *  • `shortIds` — هر چیزی که شماره دارد، رخداد یا مکان.
     *  • `priorities` — عددش ساختگیه چون کرالری در کار نیست، ولی پخشش
     *    شبیهِ واقعیه: هر سومی اصلاً عدد نداره، تا معلوم شه «بی‌عدد» ته
     *    فهرست می‌شینه نه اولش. بدونِ این، ترتیبِ ادمین توی دمو یه فهرستِ
     *    دست‌نخورده می‌شد و معلوم نمی‌شد اصلاً کار می‌کنه یا نه.
     *  • `flagged` — از همون `adminMeta` که دکمه‌ی نشان توی دمو می‌نویسدش،
     *    پس نشان گذاشتن و ترتیبِ «نشان‌دارها اول» توی دمو هم به هم وصل‌اند.
     *  • `combined` — برنده‌های ترکیب که هنوز منتشرن.
     */
    discoverMarks: (): DiscoverMarks => {
      const shortIds: Record<string, number> = {};
      for (const e of s.events) if (e.shortId != null) shortIds[e.id] = e.shortId;
      for (const b of s.businesses) if (b.shortId != null) shortIds[b.id] = b.shortId;

      const priorities: Record<string, number> = {};
      s.events.forEach((e, i) => { if (i % 3 !== 0) priorities[e.id] = (i * 37) % 151; });

      const flagged: Record<string, string> = {};
      for (const e of s.events) {
        const at = s.adminMeta[e.id]?.flaggedAt;
        if (at) flagged[e.id] = at;
      }

      return {
        shortIds,
        priorities,
        flagged,
        combined: s.combinedIds.filter((id) => s.events.some(
          (e) => e.id === id && (e.status ?? 'published') === 'published',
        )),
      };
    },

    items: (ids: string[]): EventItem[] => s.events
      .filter((e) => ids.includes(e.id))
      .map((e) => ({ ...e })),

    flagged: (): EventItem[] => s.events
      .filter((e) => !!s.adminMeta[e.id]?.flaggedAt)
      .map((e) => ({ ...e })),

    merged: (): MergedItem[] => s.events
      .filter((e) => e.status === 'merged')
      .map((e) => {
        const meta = s.adminMeta[e.id] ?? {};
        const winner = s.events.find((x) => x.id === meta.mergedInto);
        return { ...e, mergedAt: meta.mergedAt ?? null, mergedIntoShortId: winner?.shortId ?? null };
      }),

    sources: (rootId: string): ItemSource[] => {
      const thread = s.events.filter((e) => (e.rootId ?? e.id) === rootId);
      return thread.length ? [{ name: 'demo', kind: 'primary', at: thread[0].createdAt ?? null }] : [];
    },

    /**
     * حکم‌های «یکی نیستن» توی دمو، روی حافظه. کلید همون جفتِ مرتب‌شده‌ست،
     * دقیقاً مثلِ جدولِ واقعی (مایگریشن 0053).
     */
    dismissedDuplicates: (id: string): DismissedDuplicate[] => {
      const me = s.events.find((e) => e.id === id);
      if (!me) return [];
      const root = me.rootId ?? me.id;
      return s.notDuplicates
        .filter((n) => n.a === root || n.b === root)
        .map((n): DismissedDuplicate | null => {
          const otherRoot = n.a === root ? n.b : n.a;
          const other = s.events.find((e) => (e.rootId ?? e.id) === otherRoot);
          return other ? {
            id: other.id, shortId: other.shortId ?? 0, title: other.title,
            note: n.note ?? null, createdAt: n.at, byName: s.profile.displayName,
          } : null;
        })
        .filter((x): x is DismissedDuplicate => !!x);
    },

    dismissDuplicate: (a: string, b: string, note?: string): void => {
      const rootOf = (id: string) => {
        const e = s.events.find((x) => x.id === id);
        return e ? (e.rootId ?? e.id) : id;
      };
      const [lo, hi] = [rootOf(a), rootOf(b)].sort();
      if (s.notDuplicates.some((n) => n.a === lo && n.b === hi)) return;
      s.notDuplicates.push({ a: lo, b: hi, note: note?.trim() || null, at: new Date().toISOString() });
    },

    restoreDuplicate: (a: string, b: string): void => {
      const rootOf = (id: string) => {
        const e = s.events.find((x) => x.id === id);
        return e ? (e.rootId ?? e.id) : id;
      };
      const [lo, hi] = [rootOf(a), rootOf(b)].sort();
      s.notDuplicates = s.notDuplicates.filter((n) => !(n.a === lo && n.b === hi));
    },

    /**
     * ترکیب توی دمو: رکوردِ تازه از روی پایه، و بقیه merged می‌شن. انتخابِ
     * فیلدها اعمال **نمی‌شه** - چیزی که این‌جا باید ثابت شه جریانِ کاره
     * (رکوردِ تازه می‌آد، منابع می‌رن)، نه کپیِ ستون‌ها.
     */
    combine: (ids: string[]): string => {
      const base = s.events.find((e) => e.id === ids[0]);
      if (!base) return ids[0];
      const id = `combined-${Date.now()}`;
      s.events.unshift({ ...base, id, shortId: 9000 + s.events.length, status: 'published' });
      // همون `combined_from`ی که تابعِ واقعی روی ردیفِ تازه می‌ذاره - بدونش
      // فیلترِ «ترکیب‌شده‌ها»ی دیسکاور توی دمو هیچ‌وقت چیزی پیدا نمی‌کرد.
      s.combinedIds.push(id);
      for (const other of ids) {
        const row = s.events.find((e) => e.id === other);
        if (row) row.status = 'merged';
      }
      return id;
    },

    duplicates: (id: string): DuplicateCandidate[] => {
      const me = s.events.find((e) => e.id === id);
      if (!me) return [];
      const root = me.rootId ?? me.id;
      const dismissed = new Set(s.notDuplicates
        .filter((n) => n.a === root || n.b === root)
        .map((n) => (n.a === root ? n.b : n.a)));
      return s.events
        .filter((e) => (e.rootId ?? e.id) !== root
          && !dismissed.has(e.rootId ?? e.id)
          && (e.status ?? 'published') === 'published'
          && e.title.trim().toLowerCase() === me.title.trim().toLowerCase())
        .map((e) => ({
          id: e.id, shortId: e.shortId ?? 0, title: e.title, kind: e.kind ?? 'event',
          status: e.status ?? 'published', startsAt: e.startsAt ?? null, url: e.url ?? null,
          reason: 'title' as const,
        }));
    },

    actions: (rootId: string): AdminAction[] => {
      const ids = s.events.filter((e) => (e.rootId ?? e.id) === rootId).map((e) => e.id);
      return s.adminActions.filter((a) => ids.includes(a.eventId))
        .map(({ eventId, ...a }) => a).reverse();
    },

    merges: (rootId: string): ItemMerge[] => {
      const ids = s.events.filter((e) => (e.rootId ?? e.id) === rootId).map((e) => e.id);
      return s.adminMerges.filter((m) => ids.includes(m.loserId) || ids.includes(m.winnerId));
    },

    softDelete: (id: string, reason?: string): void => {
      const e = s.events.find((x) => x.id === id);
      if (!e || e.status === 'rejected') return;
      s.adminMeta[id] = { ...s.adminMeta[id], deletedFrom: e.status ?? 'published' };
      e.status = 'rejected';
      e.reviewNote = reason?.trim() || null;
      e.reviewedBy = s.profile.id;
      e.reviewedAt = new Date().toISOString();
      logAdmin(id, 'delete', { prev_status: s.adminMeta[id].deletedFrom, reason: reason ?? null });
    },

    restore: (id: string): string => {
      const e = s.events.find((x) => x.id === id);
      const prev = s.adminMeta[id]?.deletedFrom ?? 'published';
      if (!e) return prev;
      if (prev === 'published') {
        const root = e.rootId ?? e.id;
        for (const o of s.events) {
          if ((o.rootId ?? o.id) === root && o.status === 'published' && o.id !== id) o.status = 'superseded';
        }
      }
      e.status = prev as EventItem['status'];
      e.reviewNote = null;
      logAdmin(id, 'restore', { status: prev });
      return prev;
    },

    setFlag: (id: string, on: boolean, note?: string): void => {
      s.adminMeta[id] = {
        ...s.adminMeta[id],
        flaggedAt: on ? new Date().toISOString() : null,
        flagNote: on ? (note?.trim() || null) : null,
      };
      logAdmin(id, on ? 'flag' : 'unflag', { note: note ?? null });
    },

    merge: (loserId: string, winnerShortId: number, note?: string): number => {
      const loser = s.events.find((e) => e.id === loserId);
      const winner = demoStore.admin.byShortId(winnerShortId);
      if (!loser) throw new Error('آیتمِ مبدأ پیدا نشد');
      if (!winner) throw new Error(`آیتمی با شناسه‌ی ${winnerShortId} پیدا نشد`);
      const loserRoot = loser.rootId ?? loser.id;
      const winnerRow = s.events.find((e) => e.id === winner.id)!;
      if ((winnerRow.rootId ?? winnerRow.id) === loserRoot) {
        throw new Error('یه آیتم رو نمی‌شه با خودش ادغام کرد');
      }

      const thread = s.events.filter((e) => (e.rootId ?? e.id) === loserRoot
        && (e.status === 'published' || e.status === 'pending_review'));
      const statuses = thread.map((e) => ({ id: e.id, status: e.status as string }));
      const ids = s.events.filter((e) => (e.rootId ?? e.id) === loserRoot).map((e) => e.id);
      const movedFavs = s.favorites.filter((f) => ids.includes(f.refId)).map((f) => f.refId);
      s.favorites = s.favorites.filter((f) => !ids.includes(f.refId));
      if (movedFavs.length && !s.favorites.some((f) => f.refId === winner.id)) {
        s.favorites.push({ kind: winnerRow.kind ?? 'event', refId: winner.id });
      }
      for (const e of thread) {
        e.status = 'merged';
        s.adminMeta[e.id] = {
          ...s.adminMeta[e.id], mergedInto: winner.id, mergedAt: new Date().toISOString(),
        };
      }

      const id = s.nextId.admin++;
      s.adminMerges.push({
        id, loserId, loserShortId: loser.shortId ?? null, loserTitle: loser.title,
        winnerId: winner.id, winnerShortId, winnerTitle: winnerRow.title,
        note: note?.trim() || null, mergedAt: new Date().toISOString(),
        mergedByName: s.profile.displayName, undoneAt: null,
        moved: { statuses, favorites: movedFavs },
      });
      logAdmin(loserId, 'merge', { merge_id: id, winner_short_id: winnerShortId });
      return id;
    },

    unmerge: (mergeId: number): void => {
      const m = s.adminMerges.find((x) => x.id === mergeId);
      if (!m) throw new Error('ادغام پیدا نشد');
      if (m.undoneAt) throw new Error('این ادغام قبلاً برگردونده شده');
      for (const st of m.moved.statuses) {
        const e = s.events.find((x) => x.id === st.id);
        if (!e) continue;
        e.status = st.status as EventItem['status'];
        s.adminMeta[e.id] = { ...s.adminMeta[e.id], mergedInto: null, mergedAt: null };
      }
      if (m.moved.favorites.length) {
        s.favorites = s.favorites.filter((f) => f.refId !== m.winnerId);
        for (const refId of m.moved.favorites) {
          const e = s.events.find((x) => x.id === refId);
          s.favorites.push({ kind: e?.kind ?? 'event', refId });
        }
      }
      m.undoneAt = new Date().toISOString();
      logAdmin(m.loserId, 'unmerge', { merge_id: mergeId });
    },
  },

  businesses: {
    list: (): Business[] => s.businesses.map((b) => ({ ...b })),
    byId: (id: string): Business | null => s.businesses.find((b) => b.id === id) ?? null,
    suggest: (): void => undefined as unknown as void,
  },

  availability: {
    mine: (): AvailabilityCell[] => s.availability.map((c) => ({ ...c })),
    setCells: (cells: { dow: number; hour: number }[], on: boolean): void => {
      if (on) {
        for (const c of cells) {
          if (!s.availability.some((x) => x.dow === c.dow && x.hour === c.hour)) {
            s.availability.push({ dow: c.dow as AvailabilityCell['dow'], hour: c.hour, state: 'free' });
          }
        }
      } else {
        s.availability = s.availability.filter((x) => !cells.some((c) => c.dow === x.dow && c.hour === x.hour));
      }
    },
    commonSlots: (): RankedSlot[] => [
      { dow: 6, hour: 19, freeCount: 2 }, { dow: 0, hour: 14, freeCount: 1 },
    ],
  },

  notifications: {
    list: (): AppNotification[] =>
      [...s.notifications].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).map((n) => ({ ...n })),
    unreadCount: (): number => s.notifications.filter((n) => !n.readAt).length,
    unreadByCategory: (): Record<'friend_request' | 'signal' | 'event', number> => {
      const out = { friend_request: 0, signal: 0, event: 0 };
      for (const n of s.notifications) if (!n.readAt) out[categoryOfKind(n.kind)] += 1;
      return out;
    },
    markAllRead: (): void => {
      const now = new Date().toISOString();
      for (const n of s.notifications) if (!n.readAt) n.readAt = now;
    },
    markRead: (id: number): void => {
      const n = s.notifications.find((x) => x.id === id);
      if (n && !n.readAt) n.readAt = new Date().toISOString();
    },
    markSeen: (scope: 'friend' | 'rest'): void => {
      const now = new Date().toISOString();
      for (const n of s.notifications) {
        // همون سه استثنای miscRepo.markSeen: اقدام‌خواه، گفتگو/عکس، و الارم.
        if (n.readAt || ACTIONABLE_KINDS.includes(n.kind)
          || TAB_SEEN_KINDS.includes(n.kind) || n.data?.alarm === true) continue;
        const friendly = categoryOfKind(n.kind) === 'friend_request';
        if (friendly === (scope === 'friend')) n.readAt = now;
      }
    },
    /** قرینه‌ی `markSignalTabSeen` — دموْ یک برنامه بیشتر ندارد، پس فقط نوع مهم است. */
    markSignalTabSeen: (tab: 'chat' | 'photos'): void => {
      const kind = tab === 'chat' ? 'signal_message' : 'signal_photo_added';
      const now = new Date().toISOString();
      for (const n of s.notifications) if (!n.readAt && n.kind === kind) n.readAt = now;
    },
  },

  notifPrefs: {
    mine: (): NotifPrefs => ({ ...s.notifPrefs }),
    patch: (patch: Partial<NotifPrefs>): void => { s.notifPrefs = { ...s.notifPrefs, ...patch }; },
    saveToken: (): void => undefined,
    deleteToken: (): void => undefined,
  },

  release: {
    latest: (): null => null,
  },

  contacts: {
    registerMyKeys: (): void => undefined,
    /** توی دمو همیشه ثبت‌شده‌ست — همون چیزی که مایگریشن 0073 تضمین می‌کنه. */
    myEmailKey: (): string => 'demo@outami.com',
    myPhones: (): MyPhone[] => s.myPhones.map((p) => ({ ...p })),
    myPhone: (): string | null =>
      s.myPhones.find((p) => p.isDefault)?.value ?? s.myPhones[0]?.value ?? null,
    startPhoneVerification: (phone: string): StartVerifyResult => {
      const key = phoneKey(phone);
      if (!key || s.myPhones.some((p) => p.value === key)) return { status: 'mine' };
      return { status: 'sent', expiresAt: new Date(Date.now() + 600_000).toISOString() };
    },
    /**
     * توی دمو هیچ پیامکی فرستاده نمیشه، پس هیچ کدِ درستی هم وجود نداره —
     * هر شش‌رقمی قبول میشه. کدِ جادوییِ ثابت یعنی بازدیدکننده‌ی دمو باید
     * جایی حدسش بزنه؛ این‌جوری جریانِ دو مرحله‌ای رو می‌بینه بدونِ بن‌بست.
     */
    verifyPhone: (phone: string, code: string): VerifyPhoneResult => {
      const key = phoneKey(phone);
      if (!key) return { status: 'none' };
      if (!/^\d{6}$/.test(code.trim())) return { status: 'wrong', left: 4 };
      if (s.myPhones.some((p) => p.value === key)) return { status: 'mine' };
      s.myPhones.push({ value: key, isDefault: s.myPhones.length === 0 });
      return { status: 'claimed' };
    },
    setDefaultPhone: (phone: string): void => {
      if (!s.myPhones.some((p) => p.value === phone)) return;
      for (const p of s.myPhones) p.isDefault = p.value === phone;
    },
    removePhone: (phone: string): void => {
      const gone = s.myPhones.find((p) => p.value === phone);
      s.myPhones = s.myPhones.filter((p) => p.value !== phone);
      // پیش‌فرض که رفت، قدیمی‌ترینِ باقی‌مونده جاش رو می‌گیره - مثل تریگرِ سرور.
      if (gone?.isDefault && s.myPhones[0]) s.myPhones[0].isDefault = true;
    },
    matchByHash: (): [] => [],
    discovered: (): [] => [],
    list: (): ImportedContact[] => s.contacts.filter((c) => !c.hidden).map((c) => ({ ...c })),
    uploadAvatar: (base64: string): string => dataUrl(base64),
    state: (): { lastSyncedAt: string; deviceTotal: number; importedTotal: number } => ({
      lastSyncedAt: new Date(Date.now() - 200 * 3_600_000).toISOString(), deviceTotal: 8, importedTotal: 8,
    }),
    counts: (): { total: number; matched: number } => {
      const visible = s.contacts.filter((c) => !c.hidden);
      return { total: visible.length, matched: visible.filter((c) => c.matchedUserId).length };
    },
    setFriend: (contactId: number, isFriend: boolean): void => {
      const c = s.contacts.find((x) => x.id === contactId);
      if (c) c.isFriend = isFriend;
    },
    /** «هرچه از دفترچه‌ی گوشی آمده برود» — قرینه‌ی `contactFriendRepo.removeAllImported`. */
    removeImported: (): void => { s.contacts = s.contacts.filter((c) => c.source !== 'device'); },
    /** فقط برای شمارشِ ردیفِ «مخاطبینِ واردشده» — نگاه کن به `demoStore.erase`. */
    importedCount: (): number => s.contacts.filter((c) => c.source === 'device').length,
    /** ویرایشِ وب (WHOSUP-144): فقط اسم/ایمیل/شماره — عکس و شبکه‌ها دست نمی‌خورن. */
    updateBasic: (contactId: number, name: string, emails: string[], phones: string[]): void => {
      const c = s.contacts.find((x) => x.id === contactId);
      if (!c) return;
      c.displayName = name; c.emails = emails; c.phones = phones;
    },
    createManual: (draft: ManualFriendDraft, avatarPath: string | null): number => {
      const id = s.nextId.contact++;
      s.contacts.push({
        id, deviceId: `manual:${id}`, displayName: draft.name.trim(),
        firstName: null, lastName: null, emails: draft.emails.filter(Boolean), phones: draft.phones.filter(Boolean),
        relationships: [], socialProfiles: draft.socials.filter(Boolean).map((raw) => ({ service: 'other', username: raw, url: null, source: 'social' })),
        avatarPath, avatarUrl: avatarPath, company: null, isFriend: true, hidden: false,
        source: 'manual', matchedUserId: null, syncedAt: new Date().toISOString(),
      });
      return id;
    },
    updateContact: (contactId: number, draft: ManualFriendDraft, avatarPath: string | null, isFriend: boolean): void => {
      const c = s.contacts.find((x) => x.id === contactId);
      if (!c) return;
      c.displayName = draft.name.trim();
      c.emails = draft.emails.filter(Boolean);
      c.phones = draft.phones.filter(Boolean);
      c.socialProfiles = draft.socials.filter(Boolean).map((raw) => ({ service: 'other', username: raw, url: null, source: 'social' }));
      c.avatarPath = avatarPath; c.avatarUrl = avatarPath; c.isFriend = isFriend;
    },
    rematch: (): void => undefined,
    hide: (contactId: number): void => {
      const c = s.contacts.find((x) => x.id === contactId);
      if (c) c.hidden = true;
    },
    deleteAll: (): void => { s.contacts = []; },
  },

  /**
   * «حذفِ بخشی از داده‌ها» در حالتِ دمو (WHOSUP-169).
   *
   * یک گروهِ جدا و نه پخش‌شده بینِ بقیه، چون سه‌تای آخرش روی سرور یک RPC
   * هستند (مایگریشن 0136) و قرینه‌شان هم باید یک‌جا خوانده شود.
   *
   * رخداد عمداً همیشه صفر است: دفترِ دموی رخدادها همه‌اش کاتالوگِ آماده است
   * و هیچ‌کدام را «کاربرِ دمو» نفرستاده. صفر یعنی ردیفش غیرفعال می‌ماند —
   * که همان حقیقت است، نه یک دکمه‌ی بی‌اثر.
   */
  erase: {
    signalCount: (): number => s.signals
      .filter((sig) => sig.creatorId === s.profile.id && sig.status !== 'cancelled').length,
    eventCount: (): number => 0,
    messageCount: (): number => Object.values(s.messages)
      .reduce((n, list) => n + list.filter((m) => m.senderId === s.profile.id).length, 0),

    /** همان قاعده‌ی `remove_signal`، دسته‌جمعی: جواب‌گرفته لغو، بقیه حذف. */
    signals: (): void => {
      for (const sig of [...s.signals]) {
        if (sig.creatorId === s.profile.id && sig.status !== 'cancelled') {
          demoStore.signals.remove(sig.id);
        }
      }
    },
    events: (): void => undefined,
    /** متن می‌ماند، فرستنده می‌رود — قرینه‌ی `erase_my_messages`. */
    messages: (): void => {
      for (const list of Object.values(s.messages)) {
        for (const m of list) {
          if (m.senderId === s.profile.id) { m.senderId = null; m.senderName = undefined; }
        }
      }
    },
  },
};

export type { SuggestionDraft };
